import * as React from "react";
import { ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { cn } from "@/lib/utils";

/**
 * ResponsiveTable - Renders as table on desktop, card list on mobile
 */
const ResponsiveTable = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    const isMobile = useIsMobile();

    if (isMobile) {
      return (
        <div ref={ref} className={cn("card-list-mobile", className)} {...props}>
          {children}
        </div>
      );
    }

    return (
      <div className="relative w-full overflow-auto">
        <table
          ref={ref}
          className={cn("w-full caption-bottom text-sm", className)}
          {...props}
        >
          {children}
        </table>
      </div>
    );
  },
);
ResponsiveTable.displayName = "ResponsiveTable";

/**
 * ResponsiveTableHeader - Table header (hidden on mobile)
 */
const ResponsiveTableHeader = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    const isMobile = useIsMobile();

    if (isMobile) return null;

    return (
      <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props}>
        {children}
      </thead>
    );
  },
);
ResponsiveTableHeader.displayName = "ResponsiveTableHeader";

/**
 * ResponsiveTableBody - Table body / card list container
 */
const ResponsiveTableBody = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    const isMobile = useIsMobile();

    if (isMobile) {
      return (
        <div ref={ref} className={cn("space-y-3", className)} {...props}>
          {children}
        </div>
      );
    }

    return (
      <tbody
        ref={ref}
        className={cn("[&_tr:last-child]:border-0", className)}
        {...props}
      >
        {children}
      </tbody>
    );
  },
);
ResponsiveTableBody.displayName = "ResponsiveTableBody";

/**
 * ResponsiveTableRow - Table row / card item
 * On mobile, you need to provide mobileContent prop for card layout
 */
const ResponsiveTableRow = React.forwardRef(
  ({ className, children, mobileContent, onClick, ...props }, ref) => {
    const isMobile = useIsMobile();

    if (isMobile) {
      return (
        <div
          ref={ref}
          className={cn(
            "card-list-mobile-item",
            onClick && "cursor-pointer",
            className,
          )}
          onClick={onClick}
          role={onClick ? "button" : undefined}
          tabIndex={onClick ? 0 : undefined}
          {...props}
        >
          {mobileContent || children}
          {onClick && (
            <ChevronRight className="h-5 w-5 text-muted-foreground ml-auto shrink-0" />
          )}
        </div>
      );
    }

    return (
      <tr
        ref={ref}
        className={cn(
          "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
          onClick && "cursor-pointer",
          className,
        )}
        onClick={onClick}
        {...props}
      >
        {children}
      </tr>
    );
  },
);
ResponsiveTableRow.displayName = "ResponsiveTableRow";

/**
 * ResponsiveTableHead - Table head cell
 */
const ResponsiveTableHead = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        "h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-0.5",
        className,
      )}
      {...props}
    >
      {children}
    </th>
  ),
);
ResponsiveTableHead.displayName = "ResponsiveTableHead";

/**
 * ResponsiveTableCell - Table cell (hidden on mobile, use mobileContent in row)
 */
const ResponsiveTableCell = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    const isMobile = useIsMobile();

    if (isMobile) return null;

    return (
      <td
        ref={ref}
        className={cn(
          "p-2 align-middle [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-0.5",
          className,
        )}
        {...props}
      >
        {children}
      </td>
    );
  },
);
ResponsiveTableCell.displayName = "ResponsiveTableCell";

/**
 * MobileCard - Card component for mobile list items
 */
const MobileCard = React.forwardRef(
  ({ className, children, onClick, pressable = true, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "p-4 border border-border rounded-lg bg-card",
        pressable && "card-pressable",
        onClick && "cursor-pointer active:bg-muted/50",
        className,
      )}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      {...props}
    >
      {children}
    </div>
  ),
);
MobileCard.displayName = "MobileCard";

/**
 * MobileCardHeader - Header section of mobile card
 */
const MobileCardHeader = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex items-start justify-between gap-3", className)}
      {...props}
    >
      {children}
    </div>
  ),
);
MobileCardHeader.displayName = "MobileCardHeader";

/**
 * MobileCardTitle - Title for mobile card
 */
const MobileCardTitle = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn("font-medium text-foreground truncate", className)}
      {...props}
    >
      {children}
    </h3>
  ),
);
MobileCardTitle.displayName = "MobileCardTitle";

/**
 * MobileCardDescription - Description/subtitle for mobile card
 */
const MobileCardDescription = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <p
      ref={ref}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    >
      {children}
    </p>
  ),
);
MobileCardDescription.displayName = "MobileCardDescription";

/**
 * MobileCardContent - Main content area of mobile card
 */
const MobileCardContent = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn("mt-3", className)} {...props}>
      {children}
    </div>
  ),
);
MobileCardContent.displayName = "MobileCardContent";

/**
 * MobileCardMeta - Meta information row (used for stats, dates, etc.)
 */
const MobileCardMeta = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-4 mt-3 pt-3 border-t border-border text-xs text-muted-foreground",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
);
MobileCardMeta.displayName = "MobileCardMeta";

/**
 * MobileCardMetaItem - Individual meta item
 */
const MobileCardMetaItem = React.forwardRef(
  ({ className, icon: Icon, children, ...props }, ref) => (
    <span
      ref={ref}
      className={cn("flex items-center gap-1", className)}
      {...props}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {children}
    </span>
  ),
);
MobileCardMetaItem.displayName = "MobileCardMetaItem";

/**
 * HorizontalScroll - Horizontal scrolling container for mobile
 */
const HorizontalScroll = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn("horizontal-scroll", className)} {...props}>
      {children}
    </div>
  ),
);
HorizontalScroll.displayName = "HorizontalScroll";

/**
 * HorizontalScrollItem - Item within horizontal scroll
 */
const HorizontalScrollItem = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("horizontal-scroll-item", className)}
      {...props}
    >
      {children}
    </div>
  ),
);
HorizontalScrollItem.displayName = "HorizontalScrollItem";

export {
  ResponsiveTable,
  ResponsiveTableHeader,
  ResponsiveTableBody,
  ResponsiveTableRow,
  ResponsiveTableHead,
  ResponsiveTableCell,
  MobileCard,
  MobileCardHeader,
  MobileCardTitle,
  MobileCardDescription,
  MobileCardContent,
  MobileCardMeta,
  MobileCardMetaItem,
  HorizontalScroll,
  HorizontalScrollItem,
};
